import sustainabilityLevels from '@/config/sustainability';
import { getType, isDorm, isPrivate } from '@/lib/room';
import { getHighestAveragePrice, getLowestAveragePrice } from '@/lib/search/mapSearchProperty';

function toPositivePrice(value) {
  if (value === '' || value == null) {
    return null;
  }

  const numericValue = Number(value);
  return numericValue > 0 ? numericValue : null;
}

/**
 * Normalise a min/max price pair. A max at or above the ceiling means no max.
 * Reversed bounds are swapped, then the ceiling rule is applied again.
 *
 * @param {number|string|null|undefined} min
 * @param {number|string|null|undefined} max
 * @param {number} ceiling
 * @returns {{ minPrice: number|null, maxPrice: number|null }} Price bounds safe to store.
 */
export function normalisePriceRange(min, max, ceiling) {
  let minPrice = toPositivePrice(min);
  let maxPrice = toPositivePrice(max);
  const priceCeiling = Math.ceil(Number(ceiling) || 0);

  if (minPrice != null && maxPrice != null && minPrice > maxPrice) {
    [minPrice, maxPrice] = [maxPrice, minPrice];
  }

  if (priceCeiling > 0 && maxPrice != null && maxPrice >= priceCeiling) {
    maxPrice = null;
  }

  return {
    minPrice,
    maxPrice,
  };
}

export function createEmptyFilter() {
  return {
    minPrice: null,
    maxPrice: null,
    minRating: null,
    maxRating: null,
    privateEnsuite: null,
    dormEnsuite: null,
    roomType: null,
    facility: null,
    propertyType: null,
    district: null,
    promotions: null,
    minSustainabilityTier: null,
  };
}

/**
 * Parse filter params from a query string object.
 *
 * @param {Record<string, string|string[]|undefined>} query
 * @returns {object} Normalised filter state parsed from the route query.
 */
export function parse(query = {}) {
  return {
    minPrice: Number(query.minPrice) || null,
    maxPrice: Number(query.maxPrice) || null,
    minRating: Number(query.minRating) || null,
    maxRating: Number(query.maxRating) || null,
    privateEnsuite: 'privateEnsuite' in query || null,
    dormEnsuite: 'dormEnsuite' in query || null,
    roomType: query.roomType ? [].concat(query.roomType) : null,
    facility: query.facility ? [].concat(query.facility) : null,
    propertyType: query.propertyType ? [].concat(query.propertyType) : null,
    district: query.district ? [].concat(query.district).map((entry) => String(entry)) : null,
    promotions: 'promotions' in query || null,
    minSustainabilityTier: Number(query.minSustainabilityTier) || null,
  };
}

/**
 * Creates a filter predicate according to the given spec.
 *
 * @param {object} spec
 * @returns {(property: object) => boolean} Predicate that returns true when a property matches the filter.
 */
export function create(spec = {}) {
  const predicates = [];

  if (spec.minPrice) {
    predicates.push(minPrice(spec.minPrice));
  }
  if (spec.maxPrice) {
    predicates.push(maxPrice(spec.maxPrice));
  }
  if (spec.minRating) {
    predicates.push(minRating(spec.minRating));
  }
  if (spec.maxRating) {
    predicates.push(maxRating(spec.maxRating));
  }
  if (spec.propertyType) {
    predicates.push(propertyType(spec.propertyType));
  }
  if (spec.privateEnsuite) {
    predicates.push(privateEnsuite());
  }
  if (spec.dormEnsuite) {
    predicates.push(dormEnsuite());
  }
  if (spec.roomType) {
    predicates.push(spec.ensuite ? ensuiteRoomType(spec.roomType) : roomType(spec.roomType));
  }
  if (spec.facility) {
    predicates.push(facility(spec.facility));
  }
  if (spec.district) {
    predicates.push(district(spec.district));
  }
  if (spec.promotions) {
    predicates.push(promotions());
  }
  if (spec.minSustainabilityTier) {
    predicates.push(minSustainabilityTier(spec.minSustainabilityTier));
  }

  return every(...predicates);
}

function minPrice(price) {
  return (property) =>
    Math.round(getHighestAveragePrice(property.prices)) >= price
    || Math.round(getLowestAveragePrice(property.prices) || 0) >= price;
}

function maxPrice(price) {
  return (property) =>
    Math.round(getLowestAveragePrice(property.prices) || 0) <= price
    || Math.round(getHighestAveragePrice(property.prices)) <= price;
}

function minRating(rating) {
  return (property) => (property.rating?.total || 0) >= rating;
}

function maxRating(rating) {
  return (property) => (property.rating?.total || 0) <= rating;
}

function propertyType(propertyTypes) {
  const types = Array.isArray(propertyTypes) ? propertyTypes : [propertyTypes];
  return (property) => types.includes(property.type);
}

function privateEnsuite() {
  return (property) => (property.rooms || []).some((room) => room.ensuite && isPrivate(room.basicType));
}

function dormEnsuite() {
  return (property) => (property.rooms || []).some((room) => room.ensuite && isDorm(room.basicType));
}

function roomType(roomTypes) {
  return (property) =>
    (property.rooms || []).some((room) => roomTypes.includes(getType(room.basicType, room.capacity)));
}

function ensuiteRoomType(roomTypes) {
  return (property) =>
    (property.rooms || []).some(
      (room) => room.ensuite && roomTypes.includes(getType(room.basicType, room.capacity)),
    );
}

function facility(facilities) {
  return (property) => facilities.every((entry) => (property.facilities || []).includes(entry));
}

function district(districts) {
  return (property) => (property.districts || []).some((entry) => districts.includes(String(entry.id)));
}

function promotions() {
  return (property) => (property.promotions || []).length > 0;
}

function minSustainabilityTier(tier) {
  return (property) =>
    (property.categories || []).some(
      (groupCategory) =>
        groupCategory.group_category_id === 'STAIRCASE_TO_SUSTAINABILITY'
        && sustainabilityLevels[groupCategory.categories?.[0]?.category_id] >= tier,
    );
}

function every(...filters) {
  return (property) => filters.every((filterFn) => filterFn(property));
}
